import { Inject, Injectable } from '@angular/core';
import Dexie from 'dexie';
import { FileEntry } from './file-entry';
import { EncryptionService } from '@evorait/pace-encryption';
import { EncodingUtilities } from '../../utilities/encoding-utilities';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';

@Injectable()
export class FileDexieService extends Dexie {
    private static readonly VERSION_ONE = 1;
    private static readonly VERSION_TWO = FileDexieService.VERSION_ONE + 1;

    private fileEntries: Dexie.Table<FileEntry, number>;
    public constructor(
        private readonly encryption: EncryptionService,
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
    ) {
        super('FileDatabase');
        this.version(FileDexieService.VERSION_ONE).stores({
            fileEntries: 'fileName',
        });

        this.version(FileDexieService.VERSION_TWO).upgrade((trans) => {
            return trans
                .table('fileEntries')
                .toCollection()
                .modify(async (fileEntry: FileEntry) => {
                    const blob = fileEntry.content;
                    if (blob) {
                        const base64Content = await EncodingUtilities.base64EncodeBlob(blob);
                        fileEntry.encryptedContent = this.encryption.encrypt(base64Content);
                        fileEntry.mimeType = blob.type;
                        delete fileEntry.content;
                    }
                });
        });
        this.fileEntries.mapToClass(FileEntry);
    }

    /**
     * Get file from database by name
     *
     * @param fileName Name of file i.e. Title
     * @returns Promise with file in Blob format
     */
    public async getFileByName(fileName: string): Promise<{ file: Blob; timestamp: number }> {
        return this.fileEntries.get({ fileName }).then(
            (file) =>
                new Promise((resolve, reject) => {
                    if (file?.content) {
                        // this is legacy unencrypted content. so we can return it directly, but should still encrypt it in indexeddb...
                        this.writeFileEntry(file.fileName, file.content).catch((error) => this.log.error(error));
                        return resolve({ file: file.content, timestamp: file.timestamp });
                    }
                    if (file) {
                        const decryptedBase64 = this.encryption.decrypt(file.encryptedContent);
                        return resolve({
                            file: EncodingUtilities.base64DecodeBlob(decryptedBase64, file.mimeType),
                            timestamp: file.timestamp,
                        });
                    } else {
                        // retu rning code: 1 in object format in case of file not exist to meet the existing code
                        // cond itions/requirements which was written to handle the same case in ionic file storage.
                        // Ioni c file storage throws error in this format in case of file not exists.
                        reject({ code: 1 });
                    }
                }),
        );
    }

    /**
     * Write file entry to the database
     *
     * @param fileName Name of file i.e. Title
     * @param data Content of file in Blob format
     * @param timestamp time of triggering the file writing.
     * @returns Promise with the result of the write operation
     */
    public async writeFileEntry(fileName: string, data: Blob, timestamp: number = Date.now()): Promise<number> {
        // Used toDate().getTime() here on purpose to allow unit tests to manipulate the clock.
        // Used put here as it would add record if it doesn't exist by checking on fileName otherwise just updates it.
        return EncodingUtilities.base64EncodeBlob(data)
            .then((base64Content) =>
                this.fileEntries.put({
                    timestamp,
                    fileName: fileName,
                    encryptedContent: this.encryption.encrypt(base64Content),
                    mimeType: data.type,
                }),
            )
            .catch((error) => {
                throw Error(`FileDexieService: writeFileEntry: Error on writing file entry to database: ${error}`);
            });
    }

    /**
     * Remove file entry from the database.
     *
     * @param fileName Name of file i.e. Title
     * @returns Promise with the result of the delete operation
     */
    public removeFileEntry(fileName: string): Promise<number> {
        return this.fileEntries
            .where({ fileName: fileName })
            .delete()
            .catch((error) => {
                throw Error(`FileDexieService: removeFileEntry: Error on deleting the file from database: ${error}`);
            });
    }
}
